import { type ComponentProps, type FC } from 'react';
import cn from 'classnames';
import {
  type ForwardRefComponent,
  type HTMLMotionProps,
  motion,
} from 'framer-motion';

import { useCompleteContractMutation } from '@/api/queries/contract';
import { StringXSmall } from '@/components/Typography/Typography';

import css from './styles.module.scss';

type MotionDiv = ForwardRefComponent<HTMLDivElement, HTMLMotionProps<'div'>>;

interface BasePointQuantityProps {
  value: number;
}

type PointQuantityProps = BasePointQuantityProps & ComponentProps<MotionDiv>;

const PointQuantity: FC<PointQuantityProps> = ({ value, ...props }) => {
  return (
    <motion.div {...props} className={cn(css.points, props.className)}>
      <StringXSmall weight="bold" className="!text-black mt-0.5">
        {value}
      </StringXSmall>
    </motion.div>
  );
};

const withAnimation = (
  Component: FC<PointQuantityProps>,
): FC<BasePointQuantityProps> => {
  return (props: BasePointQuantityProps) => {
    const [, { isUninitialized }] = useCompleteContractMutation({
      fixedCacheKey: 'shared-complete-contract',
    });

    return (
      <Component
        initial={{ opacity: 0, scale: 1.5, x: '-50%', y: '-50%' }}
        animate={{ opacity: 1, scale: 1, x: '-50%', y: '-50%' }}
        transition={{ delay: isUninitialized ? 0.6 : 0.8, duration: 0.5 }}
        {...props}
      />
    );
  };
};

const PointQuantityWithAnimation = withAnimation(PointQuantity);

export { PointQuantity, PointQuantityWithAnimation };
